import { markRaw, type ComputedRef } from 'vue'
import type { OcrBlock, PageState } from '@/types/ainotebook'
import { fillWhite, drawTextInRect } from '@/utils/ainotebook/draw'

export function useBlockApply(opts: {
  pdfDoc: { value: any }
  currentPage: ComputedRef<PageState | null>
  renderMain: (n: number) => Promise<void>
  busyText: { value: string }
}) {
  async function applyEdits() {
    const p = opts.currentPage.value
    if (!p) return

    // mark edited + widen rect if needed (single-line style)
    const block = p.blocks.find(b => b.id) // placeholder; actual selected is edited via v-model in UI
    // NOTE: 實際要改的是「目前 selectedBlock」，但為了讓這 composable 不耦合 selection，
    // 我們在下面的 rerender 會直接畫所有 isEdited 的 blocks。
    // 所以：你的 UI 中點套用，就把 selectedBlock.isEdited = true；這邊只做 rerender。
    // => 我們把 isEdited 的設定放在下面的 "touchEditedBlocks" 內。

    // ✅ 讓快取失效（切頁回來不會用舊圖）
    p.pageUrl = ''
    p.renderRev = (p.renderRev ?? 0) + 1

    await rerenderCurrentPageWithEdits()
  }

  async function resetBlock(id: string) {
    const p = opts.currentPage.value
    if (!p) return
    const b = p.blocks.find(x => x.id === id)
    if (!b) return
    b.text = b._origText
    b.color = b._origColor
    b.fontScale = b._origFontScale
    b.isEdited = false

    // ✅ 重置後也要讓快取失效並重畫
    p.pageUrl = ''
    p.renderRev = (p.renderRev ?? 0) + 1
    await rerenderCurrentPageWithEdits()
  }

  function touchEditedBlocks(p: PageState) {
    // 讓「有任何變動」的 block 被標為 edited
    // 這裡採用保守策略：只要與 _orig 不同就 edited
    for (const b of p.blocks) {
      const changed = b.text !== b._origText || b.color !== b._origColor || b.fontScale !== b._origFontScale
      if (changed) b.isEdited = true
    }
  }

  async function rerenderCurrentPageWithEdits() {
    const p = opts.currentPage.value
    const pdfDoc = opts.pdfDoc.value
    if (!p || !pdfDoc) return

    try {
      opts.busyText.value = '重新渲染頁面…'
      const page = markRaw(await pdfDoc.getPage(p.pageNumber))
      const scale = 2.0
      const v = page.getViewport({ scale })

      const canvas = document.createElement('canvas')
      canvas.width = Math.floor(v.width)
      canvas.height = Math.floor(v.height)
      const ctx = canvas.getContext('2d')!

      fillWhite(ctx, canvas.width, canvas.height)
      await page.render({ canvasContext: ctx, viewport: v }).promise

      const baseCanvas = document.createElement('canvas')
      baseCanvas.width = canvas.width
      baseCanvas.height = canvas.height
      const baseCtx = baseCanvas.getContext('2d')!
      baseCtx.drawImage(canvas, 0, 0)

      touchEditedBlocks(p)

      for (const b of p.blocks) {
        if (!b.isEdited) continue
        drawTextInRect(ctx, baseCtx, b.text, b.rect, b.color, b.fontScale)
      }

      p.pageUrl = canvas.toDataURL('image/jpeg', 0.9)
      p.width = canvas.width
      p.height = canvas.height
    } finally {
      opts.busyText.value = '處理中…'
    }
  }

  return { applyEdits, resetBlock }
}
